/**
 * Jobs admin API — cron job configuration and manual runs.
 */

import axios from 'axios';
import { api } from '@/app/services/apiClient';
import { API_ENDPOINTS } from '@/app/config/api';

export type JobName =
  | 'partner-activation'
  | 'incomm-cardholder-export'
  | 'incomm-purse-export'
  | 'incomm-reward-export'
  | 'incomm-response-poller'
  | 'incomm-benepass-transfer'
  | 'incomm-root-reports-ingest';

export const ALL_JOB_NAMES: readonly JobName[] = [
  'partner-activation',
  'incomm-cardholder-export',
  'incomm-purse-export',
  'incomm-reward-export',
  'incomm-response-poller',
  'incomm-benepass-transfer',
  'incomm-root-reports-ingest',
] as const;

export interface JobInfo {
  job_name: JobName;
  enabled: boolean;
  disabled_reason: string | null;
  updated_at: string;
  updated_by: string | null;
  schedule: string;
}

export interface JobListResponse {
  jobs: JobInfo[];
  total: number;
}

export interface JobPatchPayload {
  enabled: boolean;
  reason: string;
}

export interface JobRunPayload {
  dry_run: boolean;
}

export type JobRunStatus = 'completed' | 'timeout' | 'error';

export interface JobRunResponse {
  job_name: JobName;
  dry_run: boolean;
  status: JobRunStatus;
  duration_seconds: number;
  result: Record<string, unknown>;
  logs: string[];
  error: string | null;
}

export interface JobDisabledConflict {
  message: string;
  disabled_reason: string | null;
}

/** Thrown when POST .../run returns 409 (job disabled, dry_run=false). */
export class JobRunDisabledError extends Error {
  readonly disabledReason: string | null;

  constructor(message: string, disabledReason: string | null) {
    super(message);
    this.name = 'JobRunDisabledError';
    this.disabledReason = disabledReason;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseJobRunConflict(error: unknown): JobDisabledConflict | null {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) {
    return null;
  }
  const data = error.response.data;
  if (!isRecord(data) || !isRecord(data.detail)) {
    return {
      message: 'Job cannot run while disabled',
      disabled_reason: null,
    };
  }
  const detail = data.detail;
  const message =
    typeof detail.message === 'string' ? detail.message : 'Job cannot run while disabled';
  const disabled_reason =
    detail.disabled_reason === null
      ? null
      : typeof detail.disabled_reason === 'string'
        ? detail.disabled_reason
        : null;
  return { message, disabled_reason };
}

export async function fetchJobs(): Promise<JobListResponse> {
  try {
    const response = await api.get<JobListResponse>(API_ENDPOINTS.admin.jobs);
    return response.data;
  } catch (error: unknown) {
    console.error('Failed to list jobs:', error);
    throw error;
  }
}

export async function patchJob(
  jobName: JobName,
  payload: JobPatchPayload
): Promise<JobInfo> {
  try {
    const response = await api.patch<JobInfo>(
      API_ENDPOINTS.admin.jobDetail(jobName),
      payload
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Failed to patch job:', error);
    throw error;
  }
}

export async function runJob(
  jobName: JobName,
  payload: JobRunPayload
): Promise<JobRunResponse> {
  try {
    const response = await api.post<JobRunResponse>(
      API_ENDPOINTS.admin.jobRun(jobName),
      payload
    );
    return response.data;
  } catch (error: unknown) {
    const conflict = parseJobRunConflict(error);
    if (conflict) {
      throw new JobRunDisabledError(conflict.message, conflict.disabled_reason);
    }
    console.error('Failed to run job:', error);
    throw new Error('Failed to run job');
  }
}
